import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from "framer-motion";
import { ArrowRight, Briefcase, MapPin, Clock, Star, Sparkles, Search, Filter, X } from "lucide-react";
import Lenis from "lenis";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CustomCursor from "../components/CustomCursor";
import TalentMatcherGame from "../components/TalentMatcherGame";
import Process from "../components/Process";
import { careers } from "../data/mock";
import SEO from "../components/SEO";
import SectionStepper from "../components/SectionStepper";

const Careers = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [cvFileName, setCvFileName] = useState("");
  const containerRef = useRef(null);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!cvFileName) {
      alert("Please upload your CV / Resume to submit your application.");
      return;
    }
    setIsSubmitted(true);
  };

  const { scrollYProgress: rawProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const scrollYProgress = useSpring(rawProgress, {
    stiffness: 80,
    damping: 25,
    restDelta: 0.001
  });

  const categories = ["All", ...new Set(careers.jobs.map(j => j.category))];

  const filteredJobs = careers.jobs.filter(job => {
    const matchesCategory = activeCategory === "All" || job.category === activeCategory;
    const matchesSearch = job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true
    });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] min-h-screen">
      <SEO 
        title="Careers | Join the Collective" 
        description="Join Meraki Solutions. Explore open positions in video editing, full-stack development, motion design, and creative strategy."
      />
      {/* <CustomCursor /> */}
      <div className="grain" />
      <Navbar />

      <main>
        <SectionStepper 
          sections={[
            { id: "top", label: "Vision" },
            { id: "values", label: "Core Values" },
            { id: "journey", label: "Journey" },
            { id: "positions", label: "Open Roles" }
          ]} 
        />
        {/* Hero */}
        <section id="top" className="relative pt-44 pb-32 overflow-hidden min-h-[90vh] flex items-center">
          {/* Hero Background Video */}
          <div className="absolute inset-0 z-0 overflow-hidden">
            <motion.div
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.5 }}
              transition={{ duration: 2.5, ease: "easeOut" }}
              className="absolute inset-0 w-full h-full"
            >
              <video
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="w-full h-full object-cover grayscale brightness-75"
              >
                <source src="https://videos.pexels.com/video-files/7693469/7693469-hd_1920_1080_25fps.mp4" type="video/mp4" />
              </video>
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)] opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-r from-[var(--ink)]/60 via-transparent to-transparent" />
          </div>

          <div className="container-xl w-full px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl"
            >
              <Kicker index="JOB-01" label="Collective Intelligence" />
              <h1 className="mt-8 font-display text-[clamp(48px,8vw,110px)] leading-[0.95] tracking-tight balance">
                Build the <span className="italic gold-text">future</span> of cinema & code.
              </h1>
              <p className="mt-10 max-w-2xl text-[var(--cream-muted)] text-xl md:text-2xl font-display italic leading-relaxed">
                {careers.hero.subtitle}
              </p>
            </motion.div>
          </div>

          {/* Background Ambient Glows */}
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-[var(--gold)] opacity-[0.03] blur-[150px] rounded-full" />
        </section>

        {/* Values Section */}
        <section id="values" className="py-32 border-t border-[rgba(201,169,97,0.1)] bg-[rgba(10,9,8,0.3)]">
          <div className="container-xl px-6">
            <div className="flex flex-col items-start mb-20 text-left">
              <Kicker index="JOB-02" label="Our Core Values" />
              <h2 className="mt-6 font-display text-5xl md:text-7xl">The Meraki <span className="italic gold-text">Soul.</span></h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
              {careers.values.map((v, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ borderColor: "#c9a961" }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: i * 0.1 }}
                  className="group relative h-[450px] rounded-[32px] overflow-hidden border-2 transition-all duration-700 shadow-2xl"
                  style={{ borderColor: "rgba(201, 169, 97, 0.4)" }}
                >
                  {/* Card Background Image */}
                  <div className="absolute inset-0">
                    <img
                      src={v.image}
                      alt={v.title}
                      className="w-full h-full object-cover opacity-60 grayscale group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-[1500ms] ease-out"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] via-[var(--ink)]/80 to-transparent transition-opacity duration-500" />
                  </div>

                  {/* Card Content */}
                  <div className="absolute inset-0 p-10 flex flex-col justify-end z-10">
                    {/* Inner Glass Glow */}
                    <div className="absolute inset-0 bg-[var(--gold)]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />

                    <div className="w-12 h-12 rounded-full border border-[var(--gold)]/40 flex items-center justify-center mb-8 group-hover:bg-[var(--gold)] group-hover:border-[var(--gold)] transition-all duration-500 scale-90 group-hover:scale-100 relative z-20">
                      <Sparkles size={20} className="text-[var(--gold)] group-hover:text-[var(--ink)]" />
                    </div>
                    <h3 className="font-display text-3xl mb-4 group-hover:text-[var(--gold)] transition-colors duration-500 relative z-20">{v.title}</h3>
                    <p className="text-[var(--cream-muted)] leading-relaxed font-light text-sm md:text-base relative z-20">{v.desc}</p>
                  </div>

                  {/* Enhanced Border Glow */}
                  <div className="absolute inset-0 border-2 border-[var(--gold)]/0 group-hover:border-[var(--gold)]/40 transition-all duration-700 pointer-events-none rounded-[32px]" />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <Process
          id="journey"
          steps={careers.process}
          title="Recruitment Journey"
          kicker={{ index: "JOB-03", label: "The Selection Process" }}
        />

        {/* Game Section */}
        <TalentMatcherGame />

        {/* Open Positions - Scroll-Driven Stacked Swipe Section */}
        <section 
          id="positions" 
          ref={containerRef}
          className="relative bg-[var(--ink-2)]"
          style={{ height: `${Math.max(filteredJobs.length, 1) * 100 + 50}vh` }}
        >
          <div className="sticky top-0 h-[100dvh] flex flex-col overflow-hidden">
            <div className="container-xl px-6 pt-24 md:pt-32 pb-4 z-10">
              <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 text-left">
                <div className="max-w-xl">
                  <Kicker index="JOB-04" label="Open Positions" />
                  <h2 className="mt-6 font-display text-4xl md:text-7xl balance">
                    Join the <span className="italic gold-text">Collective.</span>
                  </h2>
                </div>
                
              {/* Search & Filter Controls - Studio Aesthetic */}
              <div className="flex flex-col gap-8 w-full lg:w-auto">
                {/* Refined Search Bar */}
                <div className="relative group w-full lg:w-[450px]">
                  <div className="flex items-center gap-6 border-b border-white/10 group-focus-within:border-[var(--gold)] transition-all duration-700 pb-4">
                    <Search className="text-[var(--gold)]/40 group-focus-within:text-[var(--gold)] transition-colors" size={24} />
                    <input 
                      type="text"
                      placeholder="Search roles, skills, or stories..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 bg-transparent text-[var(--cream)] text-xl md:text-2xl font-display placeholder:text-[var(--muted)] focus:outline-none transition-all"
                    />
                    {searchQuery && (
                      <button 
                        onClick={() => setSearchQuery("")}
                        className="p-2 text-[var(--muted)] hover:text-[var(--gold)] transition-colors"
                      >
                        <X size={20} />
                      </button>
                    )}
                  </div>
                  {/* Expanding underline effect */}
                  <div className="absolute bottom-0 left-0 h-0.5 bg-[var(--gold)] w-0 group-focus-within:w-full transition-all duration-700 ease-out shadow-[0_0_15px_rgba(201,169,97,0.5)]" />
                </div>

                {/* Refined Category Pills */}
                <div className="flex flex-wrap gap-4">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className={`px-8 py-3 rounded-sm text-[10px] font-impact tracking-[0.3em] uppercase transition-all duration-700 ${
                        activeCategory === cat 
                          ? "bg-[var(--gold)] text-[var(--ink)] shadow-[0_10px_30px_rgba(201,169,97,0.3)]" 
                          : "bg-white/5 text-[var(--cream-muted)] hover:bg-white/10 hover:text-[var(--gold)] border-l border-white/10"
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

            <div className="flex-1 relative w-full z-10 flex items-center justify-center px-6 py-6">
              <div className="relative w-full max-w-4xl h-[50vh] min-h-[380px] max-h-[520px] md:max-h-[600px] mx-auto">
                <AnimatePresence>
                  {filteredJobs.length > 0 ? (
                    filteredJobs.map((job, i) => (
                      <JobStackCard 
                        key={job.id} 
                        job={job} 
                        index={i} 
                        total={filteredJobs.length} 
                        progress={scrollYProgress} 
                        onApply={setSelectedJob}
                      />
                    ))
                  ) : (
                    <motion.div 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 flex flex-col items-center justify-center text-center"
                    >
                      <Search size={64} className="text-[var(--gold)] opacity-20 mb-8" />
                      <h3 className="font-display text-3xl mb-4">No matching roles found</h3>
                      <button 
                        onClick={() => { setActiveCategory("All"); setSearchQuery(""); }}
                        className="mt-6 gold-text underline underline-offset-8 decoration-[var(--gold)]/30"
                      >
                        Clear all filters
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Scroll Progress Indicator */}
            <div className="container-xl pb-12 z-10">
              <div className="flex items-center justify-between gap-6">
                <div className="font-impact text-[10px] tracking-[0.4em] text-[var(--gold)]">DISCOVER ROLES</div>
                <div className="flex-1 h-px bg-[rgba(201,169,97,0.1)] relative overflow-hidden max-w-xs">
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-r from-[var(--gold)] to-[var(--gold-bright)]"
                    style={{ 
                      scaleX: useSpring(scrollYProgress, { stiffness: 100, damping: 30 }), 
                      transformOrigin: "left" 
                    }}
                  />
                </div>
                <div className="font-impact text-[10px] tracking-[0.4em] text-[var(--cream-muted)]">
                  {filteredJobs.length} TOTAL
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-44 text-center bg-[var(--ink)]">
          <div className="container-xl px-6">
            <p className="text-[var(--cream-muted)] font-display italic text-2xl mb-12 max-w-2xl mx-auto leading-relaxed">
              Don't see a role that fits? We're always looking for exceptional humans to join our cinematic frontier.
            </p>
            <a href="mailto:info@meraki-solutions.in" className="btn-primary px-16 py-6" data-cursor="contact">
              General Inquiry
            </a>
          </div>
        </section>
      </main>
      <Footer />

      {/* Apply Job Modal */}
      <AnimatePresence>
        {selectedJob && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/85 backdrop-blur-md"
            onClick={() => {
              setSelectedJob(null);
              setIsSubmitted(false);
              setCvFileName("");
            }}
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 180 }}
              className="relative w-full max-w-lg bg-[#141210] border border-[rgba(201,169,97,0.3)] rounded-[32px] p-8 md:p-12 shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                onClick={() => {
                  setSelectedJob(null);
                  setIsSubmitted(false);
                  setCvFileName("");
                }}
                className="absolute top-6 right-6 p-2 rounded-full bg-white/5 text-[var(--muted)] hover:text-white transition-colors"
              >
                <X size={20} />
              </button>

              {!isSubmitted ? (
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  <div>
                    <span className="font-impact text-[10px] tracking-[0.3em] text-[var(--gold)] uppercase">Apply Now</span>
                    <h3 className="font-display text-3xl text-white mt-2">Join the Collective</h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-impact block mb-2">Full Name</label>
                      <input 
                        type="text" required name="name" placeholder="John Doe"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--gold)] text-white transition-colors"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-impact block mb-2">Email Address</label>
                        <input 
                          type="email" required name="email" placeholder="john@example.com"
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--gold)] text-white transition-colors"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-impact block mb-2">Contact Number</label>
                        <input 
                          type="tel" required name="phone" placeholder="+91 98765 43210"
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--gold)] text-white transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-impact block mb-2">Applying Position</label>
                        <input 
                          type="text" readOnly value={selectedJob.title}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-[var(--cream-muted)] focus:outline-none cursor-default"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-impact block mb-2">Experience (Years)</label>
                        <input 
                          type="number" required min="0" max="40" name="experience" placeholder="3"
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[var(--gold)] text-white transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-impact block mb-2">Upload CV / Resume</label>
                      <div className="relative border border-dashed border-white/15 rounded-xl px-4 py-6 text-center hover:border-[var(--gold)]/50 transition-colors cursor-pointer group">
                        <input 
                          type="file" required name="cv" accept=".pdf,.doc,.docx"
                          onChange={(e) => setCvFileName(e.target.files[0]?.name || "Select PDF/DOC file")}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                        />
                        <span className="text-xs text-[var(--cream-muted)] group-hover:text-[var(--gold)] transition-colors">
                          {cvFileName || "Click to upload CV (PDF/Word)"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button type="submit" className="btn-primary w-full py-4 text-center mt-4">
                    Submit Application
                  </button>
                </form>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-[var(--gold)]/10 border border-[var(--gold)]/30 flex items-center justify-center mx-auto mb-6 animate-pulse">
                    <Sparkles className="text-[var(--gold)]" size={32} />
                  </div>
                  <h3 className="font-display text-3xl text-white mb-4">Application Submitted!</h3>
                  <p className="text-sm text-[var(--cream-muted)] leading-relaxed max-w-sm mx-auto mb-8">
                    Thank you for applying to join the Meraki Collective. Chitra and the team will review your CV and details, and get back to you shortly.
                  </p>
                  <button 
                    onClick={() => { setSelectedJob(null); setIsSubmitted(false); setCvFileName(""); }} 
                    className="btn-primary px-12"
                  >
                    Done
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const JobStackCard = ({ job, index, total, progress, onApply }) => {
  // We want each card to have its "moment" in the center.
  // Each card is active for 1/total of the scroll progress.
  const start = index / total;
  const end = (index + 1) / total;
  
  // Relative timing to ensure monotonic non-decreasing ranges
  const activeDuration = end - start;
  const entryStart = Math.max(0, start - activeDuration * 0.2);
  const exitStart = start + activeDuration * 0.8;

  // Strict Focus Logic with Safe Clamping
  const opacity = useTransform(
    progress, 
    [Math.max(0, start - 0.005), start, exitStart, end], 
    [0, 1, 1, 0]
  );
  
  const visibility = useTransform(
    progress,
    [Math.max(0, start - 0.005), start, end, Math.min(1, end + 0.005)],
    ["hidden", "visible", "visible", "hidden"]
  );

  // Motion: Stay centered, then slide away
  const y = useTransform(progress, [start, exitStart, end], [0, 0, -250]);
  const scale = useTransform(progress, [start, exitStart, end], [1, 1, 0.95]);
  const rotate = useTransform(progress, [start, exitStart, end], [0, 0, -4]);

  return (
    <motion.div
      style={{ 
        opacity, 
        visibility,
        scale, 
        y, 
        rotate,
        zIndex: total - index,
        borderColor: "rgba(201, 169, 97, 0.4)",
        pointerEvents: "auto"
      }}
      className="absolute top-0 left-0 w-full h-full rounded-[48px] overflow-hidden border-2 bg-[var(--ink)] shadow-[0_40px_100px_rgba(0,0,0,0.9)] group/card"
    >
      {/* Job Background Image */}
      <div className="absolute inset-0">
        <img 
          src={job.image} 
          alt={job.title} 
          className="w-full h-full object-cover grayscale group-hover/card:grayscale-0 group-hover/card:scale-110 transition-all duration-[2000ms] ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] via-[var(--ink)]/75 to-transparent" />
      </div>

      {/* Content */}
      <div className="absolute inset-0 p-6 md:p-12 flex flex-col justify-end z-10">
        <div className="mb-4 md:mb-6">
          <span className="px-4 py-1.5 rounded-full border border-[var(--gold)]/30 text-[var(--gold)] text-[10px] tracking-[0.2em] font-impact bg-[var(--ink)]/60 backdrop-blur-xl">
            {job.category}
          </span>
        </div>
        
        <h3 className="font-display text-2xl md:text-5xl lg:text-6xl text-[var(--cream)] mb-4 md:mb-5 leading-[1.0] tracking-tight balance">
          {job.title}
        </h3>
        
        <div className="flex flex-wrap items-center gap-10 text-[var(--cream-muted)] text-xs md:text-base font-display italic">
          <div className="flex items-center gap-3">
            <Clock size={16} className="text-[var(--gold)]" />
            <span>{job.type}</span>
          </div>
        </div>

        <div className="mt-6 md:mt-8 flex items-center justify-between">
          <motion.button 
            className="btn-primary px-8 py-3.5 md:px-12 md:py-5"
            whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(201,169,97,0.4)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onApply(job)}
          >
            Apply Now
          </motion.button>
          <Star className="text-[var(--gold)]/20 group-hover/card:text-[var(--gold)]/60 transition-colors duration-700 hidden sm:block" size={28} />
        </div>
      </div>

      {/* Glass Inner Glow on Hover */}
      <div className="absolute inset-0 bg-[var(--gold)]/5 opacity-0 group-hover/card:opacity-100 transition-opacity duration-700 pointer-events-none" />
    </motion.div>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default Careers;
